import {ItemType} from '../api/memex-items/item-type'
import {IssueFieldResources, Resources} from '../strings'
import {getEnabledFeatures} from './feature-flags'
import {getInitialState} from './initial-state'
// Capture these values in module scope to avoid repeated calls
let projectOwner: string | undefined
let issueFieldsEditable: boolean | undefined

export function resetIssueFieldCache() {
  projectOwner = undefined
  issueFieldsEditable = undefined
}

export const IssueFieldValidationReason = {
  IsDraft: 'is-draft',
  IsPullRequest: 'is-pull-request',
  ProjectOwnerMismatch: 'project-owner-mismatch',
  Ignored: 'ignored',
}
type IssueFieldValidationReason = (typeof IssueFieldValidationReason)[keyof typeof IssueFieldValidationReason]

type ValidationResult = {editable: false; reason: IssueFieldValidationReason} | {editable: true}

export function getIssueFieldErrorMessage(reason: IssueFieldValidationReason, defaultMessage?: string) {
  switch (reason) {
    case IssueFieldValidationReason.IsDraft:
      return Resources.unableToSetColumnOnDraftIssue(IssueFieldResources.issueField)
    case IssueFieldValidationReason.IsPullRequest:
      return Resources.unableToSetColumnOnPullRequest(IssueFieldResources.issueField)
    case IssueFieldValidationReason.ProjectOwnerMismatch: {
      projectOwner = getInitialState().projectOwner?.login || ''
      return projectOwner ? Resources.unableToSetIssueFieldsAcrossOwners(projectOwner) : defaultMessage
    }
    default:
      return defaultMessage
  }
}

export function getIssueFieldPasteErrorMessage(contentType: ItemType, contentOwner: string) {
  const result = getIssueFieldValidationResult(contentType, contentOwner)

  if (!result.editable) {
    return getIssueFieldErrorMessage(result.reason, Resources.genericErrorMessage)
  }

  return undefined
}

export function getIssueFieldValidationResult(contentType: ItemType, contentOwner: string): ValidationResult {
  if (issueFieldsEditable === undefined) {
    const {project_issue_fields_editing} = getEnabledFeatures()
    issueFieldsEditable = project_issue_fields_editing
  }

  if (!issueFieldsEditable) {
    return {editable: false, reason: IssueFieldValidationReason.Ignored}
  }

  if (contentType === ItemType.DraftIssue) {
    return {editable: false, reason: IssueFieldValidationReason.IsDraft}
  }

  if (contentType === ItemType.PullRequest) {
    return {editable: false, reason: IssueFieldValidationReason.IsPullRequest}
  }

  if (contentType === ItemType.RedactedItem) {
    return {editable: false, reason: IssueFieldValidationReason.Ignored}
  }

  if (projectOwner === undefined) {
    projectOwner = getInitialState().projectOwner?.login || ''
  }

  if (!projectOwner || !contentOwner) {
    return {editable: false, reason: IssueFieldValidationReason.ProjectOwnerMismatch}
  }

  if (projectOwner.toLowerCase() !== contentOwner.toLowerCase()) {
    return {editable: false, reason: IssueFieldValidationReason.ProjectOwnerMismatch}
  }

  return {editable: true}
}

export function issueFieldValuesAllowed(contentType: ItemType, contentOwner: string) {
  return getIssueFieldValidationResult(contentType, contentOwner).editable
}

export function issueFieldsEditableForTable(contentType: ItemType, contentOwner: string) {
  const result = getIssueFieldValidationResult(contentType, contentOwner)

  // Although draft issues cannot receive issue field values, cells should be editable in the table to support conversion
  return result.editable || result.reason === IssueFieldValidationReason.IsDraft
}
